import { SECTION_TYPES } from '../sectionTypes';
import { resolveLineItemAnnotationItemId } from './resolveLineItemAnnotationItemId';
import { resolvePartyAnnotationItemId } from './resolvePartyAnnotationItemId';

type Context = {
  flattenData: Record<string, unknown> | undefined;
};

/**
 * Dispatches a normalized canonical annotation path to the appropriate resolver,
 * producing a form-field id usable as a NavigableItem.id.
 *
 * - `parties.<type>(...)?` → resolvePartyAnnotationItemId
 * - `line_items.<...>` → resolveLineItemAnnotationItemId (passthrough today)
 * - bare `parties` / `line_items` (no dot) → undefined — group-level path
 *   with no row index, nothing to highlight
 * - root scalar (no dot, e.g. `order_date`) → `info.0.<scalar>` (datachecks groups
 *   info-section fields under row index 0)
 * - anything else → the normalized path itself
 *
 * Returns undefined when a sub-resolver cannot produce an id.
 *
 * Known gaps inherited from form-error handling (will be dropped by
 * remapAnnotationToFirstCell downstream):
 * - `modification_groups.*` — uiConfig section `type === 'global_modifications'`
 *   but flat key uses `modification_groups` prefix. Same dead zone exists today
 *   for form-validation errors on these fields.
 * - `custom_fields.*` — no UI rendering exists for these (datachecks's
 *   `uiConfig()` never produces a `custom` section).
 */
export const resolveAnnotationItemId = (normalizedPath: string, ctx: Context): string | undefined => {
  if (normalizedPath === SECTION_TYPES.PARTIES || normalizedPath === SECTION_TYPES.LINE_ITEMS) {
    // Bare section names (no row index, no field) can't anchor to a cell.
    return undefined;
  }

  if (normalizedPath.startsWith(`${SECTION_TYPES.PARTIES}.`)) {
    const flattenedParties = Array.isArray(ctx.flattenData?.parties)
      ? (ctx.flattenData?.parties as Array<{ type?: string }>)
      : undefined;
    return resolvePartyAnnotationItemId(normalizedPath, flattenedParties);
  }
  if (normalizedPath.startsWith(`${SECTION_TYPES.LINE_ITEMS}.`)) {
    return resolveLineItemAnnotationItemId(normalizedPath);
  }

  if (normalizedPath === '') {
    return SECTION_TYPES.INFO;
  }

  // Root scalars (no dot in the canonical path): datachecks groups these under
  // the `info` section at row index 0. Canonical `order_date` → form-field key
  // `info.0.order_date`. See datachecks/lib/ui/ui.ts `values()` for the source
  // of the `info[0]` shape.
  if (!normalizedPath.includes('.')) {
    return `${SECTION_TYPES.INFO}.0.${normalizedPath}`;
  }

  return normalizedPath;
};
